import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { FaFish } from "react-icons/fa6";

interface TokenDisplayProps {
  /** The latest token value to display */
  value: number;
  /** Duration of the tween animation in milliseconds */
  duration?: number;
}

/**
 * TokenDisplay animates (tweens) the numeric value whenever `value` prop changes.
 * It increments or decrements smoothly over the specified duration and renders
 * the number using `toLocaleString()` for thousands separators.
 */
const TokenDisplay: React.FC<TokenDisplayProps> = ({
  value,
  duration = 500,
}) => {
  const [displayValue, setDisplayValue] = useState<number>(value);

  const rootRef = useRef<HTMLDivElement>(null);

  // Expose target rect globally for fly-in animations
  useEffect(() => {
    const updateGlobalRect = () => {
      if (rootRef.current) {
        (window as any)._tokenTargetRect =
          rootRef.current.getBoundingClientRect();
      }
    };
    updateGlobalRect();
    window.addEventListener("resize", updateGlobalRect);
    return () => window.removeEventListener("resize", updateGlobalRect);
  }, []);

  const startValueRef = useRef<number>(value);
  const startTimeRef = useRef<number | null>(null);
  const frameRef = useRef<number>();

  useEffect(() => {
    // Skip animation if value hasn't changed
    if (value === displayValue) return;

    // Cancel any existing animation
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    startValueRef.current = displayValue;
    startTimeRef.current = null;

    const step = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const progress = Math.min(
        (timestamp - (startTimeRef.current as number)) / duration,
        1,
      );
      const newValue = Math.round(
        startValueRef.current + (value - startValueRef.current) * progress,
      );
      setDisplayValue(newValue);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [value, duration]);

  return (
    <Tooltip title="Tuna Tokens" arrow>
      <Box
        ref={rootRef}
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        <Typography
          variant="h6"
          component="span"
          color="#ffb74d"
          sx={{ marginRight: 0.5 }}
        >
          {displayValue.toLocaleString()}
        </Typography>
        <FaFish color="#ffb74d" />
      </Box>
    </Tooltip>
  );
};

export default TokenDisplay;
